import React, { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import TourManagementSidebar from "../components/tour/TourManagementSidebar";
import CustomRadio from "../components/common/CustomRadio";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setMealSettingsData, setPackageData } from "../reducer/tourReducer";
import { usePackage } from "../hooks/usePackage";
import { addActiveTab } from "../reducer/sideBarSlice";
import { MealSettingProps } from "../types/data";

const dietary1 = [
  "Diabetic",
  "Gluten-free",
  "Lactose-free",
  "Nut-free",
  "Seafood/fish-free",
];
const dietary2 = ["Vegitarian", "Low-carb", "Pescatarian", "Vegan", "Halal"];
const dietary3 = ["Kosher", "Egg-free", "Keto"];

const MealTypes = ["Breakfast", "Lunch", "Dinner", "Brunch"];
const MealFormat = [
  "Full meal",
  "Food tasting",
  "Cooking class",
  "Buffet",
  "Snack",
  "Picnic",
  "Packed meal",
  "BBQ",
];

function TourManagementPackageFoodPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { packageData, mealSettingsData } = useSelector(
    (state: RootState) => state.tour
  );

  const [mealType, setMealtype] = useState("");
  const [mealFormat, setMealFormat] = useState("");
  const { createPrePackage } = usePackage();
  const [loading, setLoading] = useState(false);
  const [mealErrors, setMealErrors] = useState<{
    [key: number]: { type?: boolean; format?: boolean };
  }>({});
  const progressref = useRef(7);
  const { PrepackageDataId } = useSelector((state: RootState) => state.tour);
  useOnLoadPage({ tabActive: "create" });

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.keyCode === 13) {
        handleMeal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mealFormat, mealType]);

  useEffect(() => {
    if (packageData.mealSettings) {
      try {
        const parsedData = packageData.mealSettings.map((meal) =>
          typeof meal === "string" ? JSON.parse(meal) : meal
        );
        dispatch(
          setMealSettingsData(
            parsedData.map((meal) => ({
              ...meal,
              drinksIncluded: meal.drinksIncluded || false,
            }))
          )
        );
      } catch (error) {
        console.error("Error parsing mealSettings data:", error);
      }
    }
  }, [packageData.mealSettings, dispatch]);

  const validateMeals = (): boolean => {
    if (!packageData.foodIncluded) return true;

    const errors: { [key: number]: { type?: boolean; format?: boolean } } = {};
    let isValid = true;

    parsedMealSettings.forEach((meal, index) => {
      const mealError: { type?: boolean; format?: boolean } = {};
      if (!meal.typeOfMeal || meal.typeOfMeal.trim() === "") {
        mealError.type = true;
        isValid = false;
      }
      if (!meal.format || meal.format.trim() === "") {
        mealError.format = true;
        isValid = false;
      }
      if (Object.keys(mealError).length) errors[index] = mealError;
    });

    setMealErrors(errors);
    return isValid;
  };

  const onContinue = () => {
    if (!validateMeals()) return;
    dispatch(addActiveTab(Paths.tourManagementPackageTransportation));
    navigate(Paths.tourManagementPackageTransportation);
  };

  const onSave = async () => {
    if (!validateMeals()) return;
    const updatedPackageData = {
      ...packageData,
      _id: PrepackageDataId,
      progress: Number(progressref.current),
      mealSettings: Array.isArray(parsedMealSettings)
        ? parsedMealSettings
        : [parsedMealSettings],
    };

    dispatch(setPackageData(updatedPackageData));
    setLoading(true);

    try {
      await createPrePackage(updatedPackageData);
      localStorage.setItem(
        "packageData",
        JSON.stringify({
          ...updatedPackageData,
          mealSettings: updatedPackageData.mealSettings.map((meal) =>
            typeof meal === "object" ? meal : JSON.parse(meal)
          ), // Ensure proper format in localStorage
        })
      );
      navigate(Paths.welcome);
    } catch (error) {
      console.error("Error saving package:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(Paths.tourManagementPackageGuideInfo);
  };

  const mealData = mealSettingsData || [];
  const parsedMealSettings: MealSettingProps[] = mealData.map((mealSetting) => {
    if (typeof mealSetting === "string") {
      try {
        return JSON.parse(mealSetting);
      } catch (error) {
        console.error("Failed to parse JSON string:", error);
        throw error;
      }
    } else if (typeof mealSetting === "object") {
      return mealSetting as MealSettingProps;
    } else {
      throw new Error("Invalid data format in mealSettings");
    }
  });

  const handleDietClick = (diet: string, index: number) => {
    const updatedMealSettings = parsedMealSettings.map((meal, idx) => {
      if (idx !== index) return meal;
      const currentDiet = meal.dietaryRestrictions || [];
      const updatedDiet = currentDiet.includes(diet)
        ? currentDiet.filter((d) => d !== diet)
        : [...currentDiet, diet];
      return { ...meal, dietaryRestrictions: updatedDiet };
    });
    dispatch(setMealSettingsData(updatedMealSettings));
  };

  const handleMeal = () => {
    if (!mealFormat || !mealType) return;

    const mealData = {
      typeOfMeal: mealType,
      format: mealFormat,
      dietaryRestrictions: parsedMealSettings?.[0]?.dietaryRestrictions || [],
      drinksIncluded: parsedMealSettings?.[0]?.drinksIncluded || false,
    };

    const updatedMealSettingsData = mealSettingsData
      ? [...mealSettingsData, mealData]
      : [mealData];

    dispatch(setMealSettingsData(updatedMealSettingsData));

    setMealFormat("");
    setMealtype("");
  };

  const handleRemoveMeal = (index: number) => {
    const updatedMealSettingsData = mealSettingsData.filter(
      (_, idx) => idx !== index
    );
    dispatch(setMealSettingsData(updatedMealSettingsData));
  };

  const handleDrinksIncluded = (index: number) => {
    const updatedMealSettings = parsedMealSettings.map((meal, idx) =>
      idx === index ? { ...meal, drinksIncluded: !meal.drinksIncluded } : meal
    );
    dispatch(setMealSettingsData(updatedMealSettings));
  };

  // Local state to track which meals show restrictions
  const [showRestrictions, setShowRestrictions] = useState<boolean[]>(
    parsedMealSettings.map(() => false) // initially false for each meal
  );

  const handleToggleRestriction = (index: number) => {
    setShowRestrictions((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const addNewMeal = () => {
    // NEW CHANGE: creating a completely clean meal for new entry
    const newMeal: MealSettingProps = {
      typeOfMeal: "",
      format: "",
      dietaryRestrictions: [],
      drinksIncluded: false,
      showDietaryRestrictions: false,
    };

    dispatch(setMealSettingsData([...parsedMealSettings, newMeal]));
  };

  console.log("data in meal page", parsedMealSettings);
  console.log("package data in meal page 2", packageData);

  return (
    <div className={Classname.container}>
      <Navbar />
      <div className={Classname.content}>
        <TourManagementSidebar selected={8} />

        <div className={Classname.detail}>
          <div className={Classname.title}>
            Is food included in your activity?
          </div>

          <CustomRadio
            label="No"
            selected={packageData.foodIncluded}
            value={false}
            onSelect={(value) => {
              let foodType = false;
              dispatch(
                setPackageData({
                  foodIncluded: foodType,
                })
              );
            }}
          />
          <CustomRadio
            label="Yes"
            selected={packageData.foodIncluded}
            value={true}
            onSelect={(value) => {
              dispatch(
                setPackageData({
                  foodIncluded: true,
                })
              );
            }}
          />
          {packageData.foodIncluded && (
            <>
              {parsedMealSettings.length === 0 ? (
                <div className="mt-6">
                  <div className="text-gray-600 mb-3">
                    No meal added yet. Start by adding your first meal.
                  </div>
                  <div
                    className={Classname.highlightButton}
                    onClick={() => addNewMeal()}
                  >
                    + Add meal
                  </div>
                </div>
              ) : (
                parsedMealSettings.map((meal, index) => (
                  <div key={index}>
                    <div className="flex flex-col lg:flex-row gap-5 font-semibold mt-5 mb-6">
                      <div className={Classname.dropdownContainer}>
                        <span className={Classname.dropdownTitle}>
                          Type of meal
                        </span>
                        <FormControl
                          fullWidth
                          className={mealErrors[index]?.type ? "" : "mt-1"}
                        >
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={meal.typeOfMeal || ""}
                            className="mt-1"
                            onChange={(e) => {
                              const updatedMeals = [...parsedMealSettings];
                              updatedMeals[index] = {
                                ...updatedMeals[index],
                                typeOfMeal: e.target.value,
                              };
                              dispatch(setMealSettingsData(updatedMeals));

                              if (mealErrors[index]?.type) {
                                setMealErrors((prev) => ({
                                  ...prev,
                                  [index]: { ...prev[index], type: false },
                                }));
                              }
                            }}
                          >
                            {MealTypes?.map((type, index) => (
                              <MenuItem value={type} key={index}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {mealErrors[index]?.type && (
                          <p className="text-red-500 text-sm mt-1">
                            Please select a type of meal
                          </p>
                        )}
                      </div>
                      <div className={Classname.dropdownContainer}>
                        <span className={Classname.dropdownTitle}>Format</span>
                        <FormControl
                          fullWidth
                          className={mealErrors[index]?.format ? "" : "mt-1"}
                        >
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={meal.format || ""}
                            className="mt-1"
                            onChange={(e) => {
                              const updatedMeals = [...parsedMealSettings];
                              updatedMeals[index] = {
                                ...updatedMeals[index],
                                format: e.target.value,
                              };
                              dispatch(setMealSettingsData(updatedMeals));

                              if (mealErrors[index]?.format) {
                                setMealErrors((prev) => ({
                                  ...prev,
                                  [index]: {
                                    ...prev[index],
                                    format: false,
                                  },
                                }));
                              }
                            }}
                          >
                            {MealFormat?.map((type, index) => (
                              <MenuItem value={type} key={index}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {mealErrors[index]?.format && (
                          <p className="text-red-500 text-sm mt-1">
                            Please select a format
                          </p>
                        )}
                      </div>
                      <div className="flex gap-3 items-center">
                        <div
                          className={Classname.highlightButton}
                          onClick={() => addNewMeal()}
                        >
                          + Add meal
                        </div>
                        {parsedMealSettings.length > 1 && (
                          <div
                            className={Classname.warningButton}
                            onClick={() => handleRemoveMeal(index)}
                          >
                            - Remove meal
                          </div>
                        )}
                      </div>
                    </div>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={meal.drinksIncluded || false}
                            onChange={() => handleDrinksIncluded(index)}
                          />
                        }
                        label="Drinks are included"
                      />
                    </FormGroup>

                    <CustomRadio
                      label="Show dietary restrictions"
                      selected={showRestrictions[index]}
                      value={true}
                      onSelect={() => handleToggleRestriction(index)}
                      className="mt-4 mb-5"
                    />

                    {(showRestrictions[index] ||
                      (meal?.dietaryRestrictions?.length || 0) > 0) && (
                      <>
                        <span className="mb-2 text-lg">
                          Which dietary restrictions can you accommodate?
                        </span>
                        <p>Please select all that are relevant</p>

                        <div className="lg:gap-6 flex flex-col lg:flex-row flex-wrap mt-4">
                          <div className="flex flex-col flex-1">
                            <FormGroup>
                              {dietary1.map((diet, dietIndex) => (
                                <FormControlLabel
                                  key={dietIndex}
                                  control={
                                    <Checkbox
                                      checked={
                                        meal?.dietaryRestrictions?.includes(
                                          diet
                                        ) || false
                                      }
                                      onChange={() =>
                                        handleDietClick(diet, index)
                                      }
                                    />
                                  }
                                  label={diet}
                                />
                              ))}
                            </FormGroup>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </>
          )}
          <div className="flex justify-between items-centers mt-10">
            <button className={Classname.saveButton2} onClick={handleBack}>
              Back
            </button>
            <div className={Classname.buttonsContainer}>
              <button
                className={Classname.saveButton}
                onClick={onSave}
                disabled={loading}
              >
                {loading ? "Please Wait..." : "Save and exit"}
              </button>
              <button className={Classname.continueButton} onClick={onContinue}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TourManagementPackageFoodPage;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  input: "flex w-full h-14 border border-gray-400 rounded-md px-4 mt-2",
  input2: "flex w-full h-14 border border-gray-400 rounded-md px-4 mt-1",
  dropdownContainer: "flex flex-col flex-1",
  dropdownTitle: "mb-2",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6",
  description: "flex text-base mb-4",
  buttonsContainer: "flex max-lg:justify-between gap-3 max-lg:w-full",
  highlightButton:
    "font-bold flex items-center gap-2 cursor-pointer text-primary1 py-4",
  warningButton:
    "font-bold flex items-center gap-2 cursor-pointer text-red-500 py-4",
  saveButton:
    "h-12 px-6 flex items-center lg:justify-center font-bold text-gray-700 border border-gray-500 rounded-md",
  saveButton2:
    "h-12 px-6 flex items-center lg:justify-center font-bold text-gray-700 border border-gray-500 rounded-md max-lg:hidden",
  continueButton:
    "h-12 px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md",
};
